/**
 * CoachPlaybook - Data Management Component (Mobile-Optimized)
 * Version: 0.5.146
 * Description: Mobile-first UI for backup, export, and data management with IndexedDB
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  exportAllData,
  importData,
  getStorageStats,
  getBackupStatus,
  clearAllData
} from '../../utils/db';

const DataManagement = () => {
  const [backupStatus, setBackupStatus] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [message, setMessage] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    const backup = await getBackupStatus();
    const storage = await getStorageStats();
    setBackupStatus(backup);
    setStorageStats(storage);
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleExportAll = async () => {
    const result = await exportAllData();
    if (result.success) {
      showMessage(`✅ Exported all data to ${result.fileName}`, 'success');
      loadStatus();
    } else {
      showMessage(`❌ Export failed: ${result.error}`, 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await importData(file);
      showMessage(
        `✅ Imported: ${result.itemsImported.players} players, ${result.itemsImported.plays} plays, ${result.itemsImported.opponents} opponents`,
        'success'
      );
      loadStatus();
      // Reload page to reflect imported data
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      showMessage(`❌ Import failed: ${error.error}`, 'error');
    }

    // Reset file input
    event.target.value = '';
  };

  const handleClearData = async () => {
    const result = await clearAllData();
    if (result.success) {
      showMessage('✅ All data cleared', 'success');
      setShowClearConfirm(false);
      setTimeout(() => window.location.reload(), 1500);
    } else {
      showMessage(`❌ Clear failed: ${result.error}`, 'error');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="p-4 md:p-8 bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          💾 Data Management
        </h1>
        <p className="text-sm md:text-base text-slate-400">
          Backup, export, and manage your coaching data
        </p>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`mb-4 md:mb-6 p-3 md:p-4 rounded-lg border text-sm md:text-base ${
          message.type === 'success' ? 'bg-green-900/30 border-green-700 text-green-300' :
          message.type === 'error' ? 'bg-red-900/30 border-red-700 text-red-300' :
          'bg-yellow-900/30 border-yellow-700 text-yellow-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Backup Status Card */}
      {backupStatus && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
            🔄 Backup Status
          </h2>
          <div className="space-y-2 md:space-y-3 text-sm md:text-base">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Status:</span>
              <span className={`font-semibold ${
                backupStatus.daysSinceBackup > 7 ? 'text-red-400' : 'text-green-400'
              }`}>
                {backupStatus.message}
              </span>
            </div>
            {backupStatus.hasBackup && (
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Last Backup:</span>
                <span className="font-semibold text-white">
                  {backupStatus.lastBackupDate}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Recommendation:</span>
              <span className={`font-semibold ${
                backupStatus.recommendation === 'Backup recommended' ? 'text-orange-400' : 'text-green-400'
              }`}>
                {backupStatus.recommendation}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Storage Statistics */}
      {storageStats && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
            📊 Storage Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="text-center p-3 md:p-4 bg-blue-900/30 border border-blue-800 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-blue-400">
                {storageStats.counts.players}
              </div>
              <div className="text-xs md:text-sm text-slate-400 mt-1">Players</div>
              <div className="text-xs text-slate-500 mt-1">
                {formatBytes(storageStats.sizes.players)}
              </div>
            </div>
            <div className="text-center p-3 md:p-4 bg-green-900/30 border border-green-800 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-green-400">
                {storageStats.counts.plays}
              </div>
              <div className="text-xs md:text-sm text-slate-400 mt-1">Plays</div>
              <div className="text-xs text-slate-500 mt-1">
                {formatBytes(storageStats.sizes.plays)}
              </div>
            </div>
            <div className="text-center p-3 md:p-4 bg-red-900/30 border border-red-800 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-red-400">
                {storageStats.counts.opponents}
              </div>
              <div className="text-xs md:text-sm text-slate-400 mt-1">Opponents</div>
              <div className="text-xs text-slate-500 mt-1">
                {formatBytes(storageStats.sizes.opponents)}
              </div>
            </div>
            <div className="text-center p-3 md:p-4 bg-purple-900/30 border border-purple-800 rounded-lg">
              <div className="text-2xl md:text-3xl font-bold text-purple-400">
                {storageStats.counts.stats}
              </div>
              <div className="text-xs md:text-sm text-slate-400 mt-1">Stats</div>
              <div className="text-xs text-slate-500 mt-1">
                {formatBytes(storageStats.sizes.stats)}
              </div>
            </div>
          </div>
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-700 text-center text-sm md:text-base">
            <span className="text-slate-400">Total Storage: </span>
            <span className="font-bold text-white">
              {formatBytes(storageStats.sizes.total)}
            </span>
          </div>
        </div>
      )}

      {/* Main Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Export All */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6">
          <div className="flex items-center mb-3 md:mb-4">
            <span className="text-3xl md:text-4xl mr-2 md:mr-3">📦</span>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-white">Export All Data</h3>
              <p className="text-xs md:text-sm text-slate-400">Download complete backup</p>
            </div>
          </div>
          <button
            onClick={handleExportAll}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 md:px-6 py-3 rounded-lg font-semibold hover:from-blue-500 hover:to-blue-600 transition-all shadow-lg active:scale-95"
          >
            Export Everything
          </button>
        </div>

        {/* Import Data */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6">
          <div className="flex items-center mb-3 md:mb-4">
            <span className="text-3xl md:text-4xl mr-2 md:mr-3">📥</span>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-white">Import Data</h3>
              <p className="text-xs md:text-sm text-slate-400">Restore from backup</p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-4 md:px-6 py-3 rounded-lg font-semibold hover:from-green-500 hover:to-green-600 transition-all shadow-lg active:scale-95"
          >
            Choose Backup File
          </button>
        </div>
      </div>

      {/* Clear Data */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4">
          🛠️ Data Utilities
        </h2>
        <button
          onClick={() => setShowClearConfirm(true)}
          className="w-full px-4 md:px-6 py-3 border-2 border-red-600 text-red-400 rounded-lg font-semibold hover:bg-red-900/30 transition-colors text-left flex items-center active:scale-95"
        >
          <span className="mr-2 md:mr-3 text-xl md:text-2xl">🗑️</span>
          <div className="flex-1">
            <div className="font-bold text-sm md:text-base">Clear All Data</div>
            <div className="text-xs md:text-sm text-red-500">Permanently delete all data (cannot be undone)</div>
          </div>
        </button>
      </div>

      {/* Important Notes */}
      <div className="bg-amber-900/30 border border-amber-700 rounded-xl p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-amber-300 mb-2 md:mb-3">
          ⚠️ Important: IndexedDB Storage
        </h2>
        <ul className="space-y-2 text-xs md:text-sm text-amber-200/80">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong className="text-amber-200">IndexedDB:</strong> Much more reliable than localStorage - won't be cleared by cache clearing</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong className="text-amber-200">Offline-First:</strong> Works without internet, data stays on your device</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong className="text-amber-200">Regular Backups:</strong> Export weekly to prevent data loss</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong className="text-amber-200">Device-Specific:</strong> Export/import to transfer between devices</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong className="text-amber-200">PWA Advantage:</strong> Data persists even when app is updated</span>
          </li>
        </ul>
      </div>

      {/* Clear Data Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 md:p-8 max-w-md w-full mx-4 border border-slate-700 shadow-2xl">
            <h3 className="text-xl md:text-2xl font-bold text-red-400 mb-3 md:mb-4">
              ⚠️ Confirm Data Deletion
            </h3>
            <p className="text-sm md:text-base text-slate-300 mb-4 md:mb-6">
              This will permanently delete ALL your data including:
            </p>
            <ul className="mb-4 md:mb-6 space-y-2 text-sm md:text-base text-slate-400">
              <li>• All players</li>
              <li>• All plays</li>
              <li>• All opponents</li>
              <li>• All statistics</li>
              <li>• All settings</li>
            </ul>
            <p className="text-sm md:text-base text-red-400 font-bold mb-4 md:mb-6">
              This action CANNOT be undone!
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 md:px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearData}
                className="flex-1 px-4 md:px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
              >
                Delete Everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
